import { useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useForm } from 'react-hook-form';

import { ClientProvider } from '../../types';
import { client_provider_field_options } from './clientSlice';

export type ClientProviderModalProps = {
  show: boolean;
  mode: 'new'|'edit'|'view';
  init?: ClientProvider;
  onClose: (data?: ClientProvider) => void;
  onRemove?: (data: ClientProvider) => void;
};

const initialState: ClientProvider = {
  name: 'google',
  can_signup: true,
  can_login: true,
};

export function ClientProviderModal({ mode, show, init, onClose, onRemove }: ClientProviderModalProps) {
  const { register, reset, watch, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { ...init, ...initialState },
  });

  const provider_name = watch('name');

  useEffect(() => {
    if (show) {
      reset({ ...initialState, ...init });
    }
  }, [show, reset, init]);

  const onSubmit = (provider: ClientProvider) => onClose(provider);
  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        {mode === 'new' ?
          <Modal.Title>Add Provider</Modal.Title>
          :
         mode === 'edit' ?
          <Modal.Title>Update Provider</Modal.Title>
          :
          <Modal.Title>Provider Info</Modal.Title>
        }
      </Modal.Header>
      <Modal.Body>
        <form>
          <fieldset>
            <div className="mb-3 row">
              <label htmlFor="name" className="col-sm-3 col-form-label">Name</label>
              <div className="col-sm-6">
                {mode !== 'view' ?
                <select id="name" className="form-select"
                  { ...register('name', client_provider_field_options.name) }
                >
                  <option value="google">Google</option>
                  <option value="facebook">Facebook</option>
                  <option value="line">LINE</option>
                  <option value="email">Email</option>
                </select>
                :
                <span id="name" className="form-control-plaintext">{init?.name}</span>
                }
              </div>
            </div>
            <div className="mb-3 row">
              <label htmlFor="can_signup" className="col-sm-3 col-form-label">Can Signup</label>
              <div className="mt-2 col-sm-6">
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input id="can_signup" type="checkbox" className="form-check-input"
                    { ...register('can_signup', client_provider_field_options.can_signup) }
                  />
                  :
                  <input id="can_signup" readOnly type="checkbox" className="form-check-input"
                    checked={init?.can_signup ?? initialState.can_signup}/>
                  }
                </div>
              </div>
              <span className="col-sm-3 text-danger py-1">{errors.can_signup?.message}</span>
            </div>
            <div className="mb-3 row">
              <label htmlFor="can_login" className="col-sm-3 col-form-label">Can Login</label>
              <div className="mt-2 col-sm-6">
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input id="can_login" type="checkbox" className="form-check-input"
                    { ...register('can_login', client_provider_field_options.can_login) }
                  />
                  :
                  <input id="can_login" readOnly type="checkbox" className="form-check-input"
                    checked={init?.can_login ?? initialState.can_login}/>
                  }
                </div>
              </div>
              <span className="col-sm-3 text-danger py-1">{errors.can_signup?.message}</span>
              {(provider_name === 'email' || init?.name === 'email') && <>
              <div className="mb-3 row">
                <label htmlFor="signup_provider_name" className="col-sm-3 col-form-label">Signup Provider Name</label>
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input type="text" id="signup_provider_name" className="form-control"
                    { ...register('signup_provider_name', client_provider_field_options.signup_provider_name) }
                  />
                  :
                  <span className="form-control-plaintext">{init?.signup_provider_name}</span>
                  }
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.signup_provider_name?.message}</span>
              </div>
              <div className="mb-3 row">
                <label htmlFor="signup_redirect_uri" className="col-sm-3 col-form-label">Signup Redirect URI</label>
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input type="text" id="signup_redirect_uri" className="form-control"
                    { ...register('signup_redirect_uri', client_provider_field_options.signup_redirect_uri) }
                  />
                  :
                  <span className="form-control-plaintext">{init?.signup_redirect_uri}</span>
                  }
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.signup_redirect_uri?.message}</span>
              </div>
              <div className="mb-3 row">
                <label htmlFor="login_provider_name" className="col-sm-3 col-form-label">Login Provider Name</label>
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input type="text" id="login_provider_name" className="form-control"
                    { ...register('login_provider_name', client_provider_field_options.login_provider_name) }
                  />
                  :
                  <span className="form-control-plaintext">{init?.login_provider_name}</span>
                  }
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.login_provider_name?.message}</span>
              </div>
              <div className="mb-3 row">
                <label htmlFor="login_redirect_uri" className="col-sm-3 col-form-label">Login Redirect URI</label>
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input type="text" id="login_redirect_uri" className="form-control"
                    { ...register('login_redirect_uri', client_provider_field_options.login_redirect_uri) }
                  />
                  :
                  <span className="form-control-plaintext">{init?.login_redirect_uri}</span>
                  }
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.login_redirect_uri?.message}</span>
              </div>
              <div className="mb-3 row">
                <label htmlFor="forgot_password_provider_name" className="col-sm-3 col-form-label">Forgot password Provider Name</label>
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input type="text" id="forgot_password_provider_name" className="form-control"
                    { ...register('forgot_password_provider_name', client_provider_field_options.forgot_password_provider_name) }
                  />
                  :
                  <span className="form-control-plaintext">{init?.forgot_password_provider_name}</span>
                  }
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.forgot_password_provider_name?.message}</span>
              </div>
              <div className="mb-3 row">
                <label htmlFor="forgot_password_redirect_uri" className="col-sm-3 col-form-label">Forgot password Redirect URI</label>
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input type="text" id="forgot_password_redirect_uri" className="form-control"
                    { ...register('forgot_password_redirect_uri', client_provider_field_options.forgot_password_redirect_uri) }
                  />
                  :
                  <span className="form-control-plaintext">{init?.forgot_password_redirect_uri}</span>
                  }
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.forgot_password_redirect_uri?.message}</span>
              </div>
              </>}
            </div>
          </fieldset>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={() => onClose()}>
          Close
        </button>
        {mode === 'edit' ?
        <button type="submit" className="btn btn-danger" onClick={() => onRemove && onRemove(init ?? initialState)}>
          Remove
        </button>
        : null}
        {mode !== 'view' ?
        <button type="submit" className="btn btn-primary" onClick={(e) => handleSubmit(onSubmit)(e)}>
          {init ? 'Update' : 'Add' }
        </button>
        : null}
      </Modal.Footer>
    </Modal>
  );
}
